import { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import { createPortal } from 'react-dom';
import { apiUrl } from './config';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function SpotComboBox(props) {
  const getNormalizedSpotValue = (value) => {
    if (Array.isArray(value)) {
      return value[0] || null;
    }

    return value || null;
  };

  const [query, setQuery] = useState('')
  const [selectedSpot, setSelectedSpot] = useState(getNormalizedSpotValue(props.value))
  const [loadedSpot, setLoadedSpot] = useState(null);

  const add = props.add;
  const spots = props.spots || []; // Default to empty array if spots is null
  const containerClassName = props.className || 'w-40 max-w-full';
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Update the useEffect to better handle external value changes
  useEffect(() => {
    console.log('SpotComboBox value prop changed:', props.value);
    console.log('Current selectedSpot:', selectedSpot);

    const externalSpotValue = getNormalizedSpotValue(props.value);
    
    setSelectedSpot(externalSpotValue);
    
    // Always load spot details when value is set externally
    if (externalSpotValue && (externalSpotValue._id || externalSpotValue.id || externalSpotValue.spot_id)) {
      console.log('Loading details for externally set spot:', externalSpotValue);
      // Use a slight delay to ensure state is updated
      setTimeout(() => {
        LoadSpotDetails(externalSpotValue);
      }, 100);
    } else {
      console.log('Clearing loaded spot - no value or no ID');
      setLoadedSpot(null);
    }
  }, [props.value]);

  // Load spot details
  function LoadSpotDetails(selectedSpot) {
    console.log('Loading spot details for:', selectedSpot);
    if (selectedSpot === null) {
      console.log('Selected spot is null, clearing loaded spot');
      setLoadedSpot(null);
      return;
    }

    // Get the spot ID - handle multiple possible ID fields
    const spotId = selectedSpot._id || selectedSpot.id || selectedSpot.spot_id;
    if (!spotId) {
      console.error('No spot ID found in selectedSpot:', selectedSpot);
      return;
    }

    const fetchSpot = async () => {
      try {
        console.log('Fetching spot with ID:', spotId);
        const response = await fetch(apiUrl(`/media/get/${spotId}`));
        if (response.ok) {
          var localLoadedSpot = await response.json();
          console.log('Loaded spot details:', localLoadedSpot);
          setLoadedSpot(localLoadedSpot);
          
          // Debug the spot fields
          console.log('Spot fields:', {
            cart: localLoadedSpot.cart,
            cut: localLoadedSpot.cut,
            spot_title: localLoadedSpot.spot_title,
            description: localLoadedSpot.description,
            status: localLoadedSpot.status,
            run_length: localLoadedSpot.run_length,
            customer_name: localLoadedSpot.customer_name
          });
        } else {
          console.error('Failed to load spot details, status:', response.status);
          setLoadedSpot(null);
        }
      } catch (error) {
        console.error('Error loading spot details:', error);
        setLoadedSpot(null);
      }
    }

    fetchSpot();
  }

  // Add a debug useEffect to track loadedSpot changes
  useEffect(() => {
    console.log('LoadedSpot changed:', loadedSpot);
  }, [loadedSpot]);

  // Search spots from API when typing
  useEffect(() => {
    const searchSpots = async () => {
      if (!query || query.trim() === '') {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetch(apiUrl('/media/search'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ term: query }),
          mode: 'cors',
        });

        if (response.ok) {
          const data = await response.json();
          const normalizedResults = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : []);
          setSearchResults(normalizedResults);
        } else {
          console.error('Spot search failed:', response.status, response.statusText);
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching spots:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimeout = setTimeout(() => {
      searchSpots();
    }, 250);

    return () => clearTimeout(debounceTimeout);
  }, [query]);

  const setSelectedSpotHandler = (spot) => {
    console.log('Spot selected in ComboBox:', spot);
    setSelectedSpot(spot);
    LoadSpotDetails(spot); // Re-enable this to load details when selected
    if (props.onChange) {
      // Create an event-like object with the spot as the value
      const syntheticEvent = {
        target: { value: spot },
        currentTarget: { value: spot },
        value: spot
      };
      props.onChange(syntheticEvent);
    }
  }

  const handleAddSpot = () => {
    console.log('Add Spot');
    window.location.href = '/spotadd';
  }

  let addButton = <div></div>;

  if (add) {
    addButton = (
      <div className="mt-2 pl-2 grow-0 table-cell">
        <button type="button"
                onClick={handleAddSpot}
                className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
            Add
        </button>
      </div>
    );
  }

  // Helper function to get display text for a spot
  const getSpotDisplayText = (spot) => {
    if (!spot) return '';
    
    const cart = spot.cart || '';
    const cut = spot.cut || '';
    
    const spot_id = `${cart}${!cut ? '' : '-' + cut}`;

    return spot_id;
  };

  // Filter spots by title or cart number - Re-enable filtering
  const filteredSpots =
    query === ''
      ? spots.slice(0, 50) // Limit to first 50 spots when no query to improve performance
      : searchResults;

  const updateDropdownPosition = () => {
    const anchorElement = inputRef.current || wrapperRef.current;

    if (!anchorElement) {
      return;
    }

    const rect = anchorElement.getBoundingClientRect();
    const desiredWidth = rect.width * 1.5;
    const maxWidth = window.innerWidth - rect.left - 8;
    const dropdownWidth = Math.max(rect.width, Math.min(desiredWidth, maxWidth));

    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: dropdownWidth,
    });
  };

  useEffect(() => {
    if (!showDropdown) {
      return undefined;
    }

    updateDropdownPosition();

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [showDropdown, query, filteredSpots.length]);

  // Debug logging to troubleshoot the dropdown issue
  console.log('Total spots:', spots.length);
  console.log('Filtered spots:', filteredSpots.length);
  console.log('Is searching:', isSearching);
  console.log('Query:', query);
  
  return (
    <div className={containerClassName}>
      <Combobox as="div" value={selectedSpot} onChange={setSelectedSpotHandler}>
        <div ref={wrapperRef} className="relative w-full table-cell">
          <Combobox.Input
            ref={inputRef}
            className="w-full rounded-md border-0 bg-white py-1 pl-2 pr-8 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            displayValue={(spot) => getSpotDisplayText(spot)}
            placeholder="Select Spot"
          />
          <Combobox.Button
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
            onClick={() => {
              setShowDropdown(true);
              updateDropdownPosition();
            }}
          >
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>

          {showDropdown && filteredSpots.length > 0 && createPortal(
            <Combobox.Options
              className="fixed z-[9999] max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-base shadow-2xl ring-1 ring-black ring-opacity-10 focus:outline-none sm:text-sm"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
            >
              {filteredSpots.map((spot) => {
                const uniqueKey = spot._id || spot.id || spot.spot_id || `spot-${spot.cart}-${spot.cut}` || Math.random();
                
                return (
                  <Combobox.Option
                    key={uniqueKey}
                    value={spot}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-default select-none py-3 pl-3 pr-9',
                        active ? 'bg-indigo-600 text-white' : 'text-gray-900 hover:bg-gray-50'
                      )
                    }
                  >
                    {({ active, selected }) => (
                      <>
                        <div className="flex flex-col">
                          <span className={classNames('block truncate', selected && 'font-semibold')}>
                            {spot.spot_title || spot.description || 'Untitled Spot'}
                          </span>
                          <span className={classNames('block truncate text-xs mt-1', active ? 'text-gray-200' : 'text-gray-500')}>
                            Cart: {spot.cart || 'N/A'}{spot.cut ? `-${spot.cut}` : ''} • {spot.status || 'No status'} • {spot.run_length || 'N/A'}s
                          </span>
                        </div>

                        {selected && (
                          <span
                            className={classNames(
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                              active ? 'text-white' : 'text-indigo-600'
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                );
              })}
            </Combobox.Options>,
            document.body
          )}
        </div>
        {addButton}
      </Combobox>
    </div>
  )
}